import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table,
  Header,
  SpaceBetween,
  Button,
  Badge,
  Box,
  Modal,
  Form,
  FormField,
  Input,
  Multiselect,
  Container,
  Alert
} from '@cloudscape-design/components'
import { apiService } from '../services/api'

interface User {
  id: string
  username: string
  email: string
  name: string
  role: string
  status: 'active' | 'inactive' | 'pending'
  authorizedDataProducts: string[]
  deviceCount: number
  lastLogin: string
  createdAt: string
}

// Map Cognito UserStatus to our display status
const mapCognitoStatus = (cognitoStatus: string | undefined): 'active' | 'inactive' | 'pending' => {
  switch (cognitoStatus) {
    case 'CONFIRMED': return 'active'
    case 'UNCONFIRMED': return 'pending'
    case 'FORCE_CHANGE_PASSWORD': return 'pending'
    default: return 'inactive'
  }
}

const Users: React.FC = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedItems, setSelectedItems] = useState<User[]>([])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  const dataProductOptions = [
    { label: 'METAR Weather', value: 'metar' },
    { label: 'Marine Conditions', value: 'marine' },
    { label: 'Trail Conditions', value: 'trail' },
    { label: 'NWS Observations', value: 'nws' },
    { label: 'HF Radio Propagation', value: 'hf_propagation' },
    { label: 'Beach Conditions', value: 'beach_conditions' }
  ]

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    setLoading(true)
    try {
      const response = await apiService.getUsers()
      
      if (response.statusCode === 200 && response.data) {
        // Handle different response formats from the API
        let usersData = response.data
        
        // If the response has a nested structure, extract the users array
        if (response.data && typeof response.data === 'object' && 'users' in response.data) {
          usersData = (response.data as any).users
        } else if (Array.isArray(response.data)) {
          usersData = response.data
        } else {
          // If it's a single object, wrap it in an array
          usersData = [response.data]
        }
        
        // Transform API data to match our interface
        const transformedUsers = usersData.map((user: any) => {
          // Build a human-readable name, never show Cognito sub/UUID hashes
          const givenName = user.given_name || user.givenName || '';
          const familyName = user.family_name || user.familyName || '';
          const fullName = [givenName, familyName].filter(Boolean).join(' ');
          const displayName = fullName || user.email?.split('@')[0] || 'Unknown User';

          return {
          id: user.user_id || user.userId || user.id,
          username: user.email || user.email?.split('@')[0] || 'unknown',
          email: user.email,
          name: displayName,
          role: user.role || 'User',
          status: mapCognitoStatus(user.status),
          authorizedDataProducts: user.authorized_data_products || user.authorizedDataProducts || [],
          deviceCount: user.device_count || user.deviceCount || 0,
          lastLogin: user.last_login || user.lastLogin || 'Never',
          createdAt: user.created_at || user.createdAt || new Date().toISOString().split('T')[0]
          };
        })
        setUsers(transformedUsers)
      } else {
        // Handle all non-200 status codes
        let errorMessage = 'Failed to load users'
        
        if (response.statusCode === 401) {
          errorMessage = 'Authentication failed - please login again (HTTP 401)'
        } else if (response.statusCode === 403) {
          errorMessage = 'Insufficient permissions - admin access required (HTTP 403)'
        } else if (response.statusCode === 500) {
          errorMessage = 'Server error - Lambda function may be experiencing issues (HTTP 500)'
        } else if (response.statusCode === 502) {
          errorMessage = 'Bad Gateway - API Gateway or Lambda error (HTTP 502)'
        } else {
          errorMessage = `${response.error || 'Unknown error'} (HTTP ${response.statusCode})`
        }
        
        throw new Error(errorMessage)
      }
    } catch (error) {
      console.error('Error loading users:', error)
      console.log('Setting alert state with error:', error instanceof Error ? error.message : 'Failed to load users. Please try again.')
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load users. Please try again.'
      })
      // Set empty array as fallback
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge color="green">Active</Badge>
      case 'inactive':
        return <Badge color="red">Inactive</Badge>
      case 'pending':
        return <Badge color="grey">Pending Approval</Badge>
      default:
        return <Badge>Unknown</Badge>
    }
  }

  const handleEditUser = (user: User) => {
    setEditingUser(user)
    setShowEditModal(true)
  }

  const handleSaveUser = async () => {
    if (!editingUser) return

    try {
      const response = await apiService.updateUserDataProducts(
        editingUser.id, 
        editingUser.authorizedDataProducts
      )
      
      if (response.statusCode === 200) {
        setUsers(users.map(u => u.id === editingUser.id ? editingUser : u))
        setShowEditModal(false)
        setEditingUser(null)
        setAlert({
          type: 'success',
          message: 'User updated successfully'
        })
      } else {
        let errorMessage = 'Failed to update user'
        
        if (response.statusCode === 401) {
          errorMessage = 'Authentication failed - please login again (HTTP 401)'
        } else if (response.statusCode === 403) {
          errorMessage = 'Insufficient permissions to update user (HTTP 403)'
        } else if (response.statusCode === 500) {
          errorMessage = 'Server error while updating user (HTTP 500)'
        } else {
          errorMessage = `${response.error || 'Failed to update user'} (HTTP ${response.statusCode})`
        }
        
        throw new Error(errorMessage)
      }
    } catch (error) {
      console.error('Error updating user:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update user. Please try again.'
      })
    }
  }

  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) return

    try {
      // Approve all selected users by giving them access to all data products
      const promises = selectedItems.map(user => 
        apiService.updateUserDataProducts(user.id, ['metar', 'marine', 'trail'])
      )
      
      const responses = await Promise.all(promises)
      
      // Check if any requests failed
      const failedResponses = responses.filter(response => response.statusCode !== 200)
      
      if (failedResponses.length > 0) {
        const firstError = failedResponses[0]
        let errorMessage = 'Failed to approve users'
        
        if (firstError.statusCode === 401) {
          errorMessage = 'Authentication failed - please login again (HTTP 401)'
        } else if (firstError.statusCode === 403) {
          errorMessage = 'Insufficient permissions to approve users (HTTP 403)'
        } else if (firstError.statusCode === 500) {
          errorMessage = 'Server error while approving users (HTTP 500)'
        } else {
          errorMessage = `${firstError.error || 'Failed to approve users'} (HTTP ${firstError.statusCode})`
        }
        
        throw new Error(errorMessage)
      }
      
      // Reload users to reflect changes
      await loadUsers()
      
      setSelectedItems([])
      setAlert({
        type: 'success',
        message: `Successfully approved ${selectedItems.length} user(s)`
      })
    } catch (error) {
      console.error('Error approving users:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to approve users. Please try again.'
      })
    }
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              disabled={selectedItems.length === 0}
              onClick={handleBulkApprove}
            >
              Approve Selected
            </Button>
            <Button 
              variant="primary"
              onClick={loadUsers}
              loading={loading}
            >
              Refresh
            </Button>
          </SpaceBetween>
        }
      >
        User Management
      </Header>

      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}
      
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'name',
              header: 'Name',
              cell: item => item.name,
              sortingField: 'name'
            },
            {
              id: 'email',
              header: 'Email',
              cell: item => item.email,
              sortingField: 'email'
            },
            {
              id: 'role',
              header: 'Role',
              cell: item => {
                const colors: Record<string, 'red' | 'blue' | 'grey'> = { Admin: 'red', Production: 'blue', User: 'grey' }
                return <Badge color={colors[item.role] || 'grey'}>{item.role}</Badge>
              },
              sortingField: 'role'
            },
            {
              id: 'status',
              header: 'Status',
              cell: item => getStatusBadge(item.status)
            },
            {
              id: 'dataProducts',
              header: 'Data Products',
              cell: item => (
                <SpaceBetween direction="horizontal" size="xs">
                  {item.authorizedDataProducts.map(product => (
                    <Badge key={product} color="blue">
                      {dataProductOptions.find(opt => opt.value === product)?.label || product}
                    </Badge>
                  ))}
                  {item.authorizedDataProducts.length === 0 && (
                    <Badge color="grey">None</Badge>
                  )}
                </SpaceBetween>
              )
            },
            {
              id: 'devices',
              header: 'Devices',
              cell: item => item.deviceCount,
              sortingField: 'deviceCount'
            },
            {
              id: 'lastLogin',
              header: 'Last Login',
              cell: item => item.lastLogin,
              sortingField: 'lastLogin'
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: item => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="inline-link"
                    onClick={() => handleEditUser(item)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="inline-link"
                    onClick={() => navigate(`/location-lists?userId=${item.id}`)}
                  >
                    Location Lists
                  </Button>
                </SpaceBetween>
              )
            }
          ]}
          items={users}
          loading={loading}
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          selectionType="multi"
          trackBy="id"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No users</b>
              <Box variant="p" color="inherit">
                No users have been registered yet.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={`(${users.length})`}
              description="Manage user accounts and data product access"
            >
              Users
            </Header>
          }
        />
      </Container>

      <Modal
        visible={showEditModal}
        onDismiss={() => setShowEditModal(false)}
        header="Edit User"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveUser}>
                Save Changes
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {editingUser && (
          <Form>
            <SpaceBetween direction="vertical" size="l">
              <FormField label="Email">
                <Input
                  value={editingUser.email}
                  disabled
                />
              </FormField>
              <FormField label="Name">
                <Input
                  value={editingUser.name}
                  onChange={({ detail }) =>
                    setEditingUser({ ...editingUser, name: detail.value })
                  }
                />
              </FormField>
              <FormField label="Authorized Data Products">
                <Multiselect
                  selectedOptions={editingUser.authorizedDataProducts.map(product => ({
                    label: dataProductOptions.find(opt => opt.value === product)?.label || product,
                    value: product
                  }))}
                  onChange={({ detail }) =>
                    setEditingUser({
                      ...editingUser,
                      authorizedDataProducts: detail.selectedOptions.map(opt => opt.value!)
                    })
                  }
                  options={dataProductOptions}
                  placeholder="Select data products"
                />
              </FormField>
            </SpaceBetween>
          </Form>
        )}
      </Modal>
    </SpaceBetween>
  )
}

export default Users